import React from "react";
import ScrollReveal from "./ScrollReveal";
import { Bell, LifeBuoy, CalendarHeart, Users } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const FEATURES = [
    { Icon: Bell,          labelKey: "discord.featureWipes" },
    { Icon: LifeBuoy,      labelKey: "discord.featureSupport" },
    { Icon: CalendarHeart, labelKey: "discord.featureEvents" },
    { Icon: Users,         labelKey: "discord.featureCommunity" },
];

// Discord-like glyph (svg) to avoid generic lucide
const DiscordGlyph = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M20.317 4.369a19.8 19.8 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.056a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.105a13.1 13.1 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127a12.3 12.3 0 0 1-1.873.893a.077.077 0 0 0-.041.105c.36.699.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.84 19.84 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.055c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028Zm-12.5 10.955c-1.183 0-2.157-1.086-2.157-2.42c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.095 2.157 2.42c0 1.333-.956 2.419-2.157 2.419Zm7.975 0c-1.183 0-2.157-1.086-2.157-2.42c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.095 2.157 2.42c0 1.333-.946 2.419-2.157 2.419Z" />
    </svg>
);

export default function DiscordCTA({ discordUrl }) {
    const { t } = useI18n();
    const titleAfter = t("discord.titleAfter");
    return (
        <section
            id="discord"
            data-testid="discord-section"
            className="relative py-24 md:py-32 px-6 md:px-8"
        >
            <div className="max-w-5xl mx-auto">
                <ScrollReveal>
                    <div
                        className="relative overflow-hidden rounded-3xl border border-white/10 p-8 md:p-14"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(42,24,16,0.92) 0%, rgba(10,7,6,0.95) 55%, rgba(26,15,10,0.92) 100%)",
                        }}
                    >
                        {/* Gold glow */}
                        <div
                            className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(circle, rgba(255,184,0,0.2) 0%, transparent 70%)",
                                filter: "blur(20px)",
                            }}
                        />
                        {/* Discord glyph watermark — decorative, behind features */}
                        <DiscordGlyph
                            className="hidden md:block absolute top-1/2 right-0 translate-x-[18%] -translate-y-1/2 w-[420px] h-[420px] pointer-events-none"
                            style={{
                                color: "rgba(255, 184, 0, 0.13)",
                                filter:
                                    "drop-shadow(0 0 50px rgba(255,184,0,0.12))",
                            }}
                        />
                        {/* Mobile version — tucked into bottom-right corner, more visible */}
                        <DiscordGlyph
                            className="md:hidden absolute -bottom-8 -right-8 w-32 h-32 pointer-events-none"
                            style={{ color: "rgba(255, 184, 0, 0.12)" }}
                        />

                        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.4em] text-gold/80 mb-3">
                                    {t("discord.eyebrow")}
                                </div>
                                <h2
                                    data-testid="discord-title"
                                    className="text-3xl md:text-5xl font-bold text-zrs-offwhite leading-[1.05]"
                                >
                                    {t("discord.titleBefore")}{" "}
                                    <span className="text-gold">{t("discord.titleHighlight")}</span>
                                    {titleAfter ? <>{" "}{titleAfter}</> : null}
                                </h2>
                                <p className="mt-5 text-[14px] md:text-[15px] text-zrs-offwhite/70 max-w-md leading-relaxed">
                                    {t("discord.desc")}
                                </p>

                                <a
                                    href={discordUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="discord-join-btn"
                                    className="btn-shimmer mt-8 inline-flex items-center gap-3 px-6 py-3.5 rounded-full font-bold text-[14px] tracking-wide transition-transform hover:scale-[1.03]"
                                    style={{ background: "#FFB800", color: "#050505" }}
                                >
                                    <DiscordGlyph className="w-5 h-5" />
                                    {t("discord.join")}
                                </a>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {FEATURES.map(({ Icon, labelKey }, i) => (
                                    <div
                                        key={i}
                                        data-testid={`discord-feature-${i}`}
                                        className="rounded-xl p-4 border border-white/10 gold-border-hover"
                                        style={{
                                            background: "rgba(0,0,0,0.35)",
                                        }}
                                    >
                                        <div
                                            className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 border"
                                            style={{
                                                background: "rgba(255,184,0,0.08)",
                                                borderColor: "rgba(255,184,0,0.2)",
                                            }}
                                        >
                                            <Icon size={16} className="text-gold" />
                                        </div>
                                        <div className="text-[13px] font-semibold text-zrs-offwhite">
                                            {t(labelKey)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );
}
